// @flow
import classnames from 'classnames';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/styles';

import ElectronLayout from './ElectronLayout';
import Header from '../components/Header';
import SocketReconnector from '../components/Network/SocketReconnector';
import Sidebar from '../components/Sidebar';

import type { CCMode } from '../types/cc-mode';
import { boot, hideDefaultLauncher, shutdown } from '../utils/intercom';

const useStyles = makeStyles(theme => ({
  root: {
    background: theme.palette.background.default,
    display: 'grid',
    gridTemplateColumns: `
      [sidebar-start] minmax(260px, max-content) [sidebar-end]
      repeat(12, [col-start] 1fr [col-end])
    `,
    gridTemplateRows: `
      [appBar-start] max-content [appBar-end
      header-start] minmax(67px, max-content) [header-end
      main-start] 1fr [main-end]
    `,
    height: '100vh',
    width: '100vw',
  },
  columnSpacing: {
    columnGap: 10,
  },
  electronRoot: {
    gridTemplateRows: `
      [appBar-start] max-content [appBar-end
      header-start] minmax(99px, max-content) [header-end
      main-start] 1fr [main-end]
    `,
  },
  rootSidebarClosed: {
    gridTemplateColumns: `
      [sidebar-start] 0 [sidebar-end]
      repeat(12, [col-start] 1fr [col-end])
    `,
  },
  rootSidebarClosedAuthenticated: {
    gridTemplateColumns: `
      [sidebar-start] max-content [sidebar-end]
      repeat(12, [col-start] 1fr [col-end])
    `,
  },
  appBar: {
    gridColumn: 'sidebar-start / col-end 12',
    gridRow: 'appBar-start / appBar-end',
  },
  header: {
    gridColumn: 'col-start / col-end 12',
    gridRow: 'header-start / header-end',
  },
  sidebar: {
    gridColumn: 'sidebar-start / sidebar-end',
    gridRow: 'header-start / main-end',
  },
  main: {
    gridColumn: 'col-start / col-end 12',
    gridRow: 'main-start / main-end',
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflow: 'auto',
  },
}));

interface Props {
  ccMode: CCMode;
  children: Object;
  firebaseUser: Object;
  isInConversation: boolean;
  status: Object;
  user: Object;
  sidebar: boolean;
  electronCaptionMode: boolean;
  sessionLoading: boolean;
}

const Default = ({
  ccMode,
  children,
  firebaseUser,
  isInConversation,
  status,
  user,
  sidebar,
  sessionLoading,
  electronCaptionMode,
}: Props) => {
  const classes = useStyles();
  const theme = useTheme();

  useEffect(() => {
    if (electronCaptionMode) {
      shutdown();
    } else {
      boot({
        app_id: 'eko73i1t',
        created_at: new Date(),
        user_id: user?.avaId,
        email: firebaseUser?.email,
        hide_default_launcher: isInConversation,
      });
    }
  }, [electronCaptionMode, ccMode]);

  useEffect(() => {
    // If in CC mode window, we don't want the logic for
    // the intercom button to appear.
    if (!electronCaptionMode) {
      hideDefaultLauncher(isInConversation);
    }
  }, [isInConversation]);

  return (
    <>
      <div
        className={classnames(
          { [classes.root]: !electronCaptionMode },
          { [classes.columnSpacing]: firebaseUser?.isAnonymous || isInConversation },
          {
            [!firebaseUser?.isAnonymous ? classes.rootSidebarClosedAuthenticated : classes.rootSidebarClosed]:
              !sidebar && !electronCaptionMode,
          },
          { [classes.electronRoot]: firebaseUser?.isAnonymous && window.isElectron && !electronCaptionMode }
        )}
      >
        {window.isElectron && !electronCaptionMode && <ElectronLayout className={classes.appBar} />}
        <Header
          className={classnames({
            [classes.header]: !electronCaptionMode,
          })}
        />
        {!electronCaptionMode && (
          <Sidebar
            className={classnames({
              [classes.sidebar]: !electronCaptionMode,
            })}
            sidebarShown={sidebar}
            allowSaving={
              !!status.confidential &&
              status.confidential.allowTranscriptsSaving &&
              !!user.features &&
              !user.features['forbid-conversation-saving']
            }
          />
        )}
        <main
          id="main-content" // Needed for lazy load transcripts
          className={classnames({
            [classes.main]: !electronCaptionMode,
          })}
          style={{
            backgroundColor: electronCaptionMode ? 'inherit' : theme.palette.background.default,
          }}
        >
          {!sessionLoading && <SocketReconnector />}
          {children}
        </main>
      </div>
    </>
  );
};

const mapStateToProps = ({
  ccMode: { ccMode },
  firebase: { user: firebaseUser },
  scribeConversation: { isInConversation, status },
  scribeSession: { captioner, loading, sidebar },
  scribeTranscriptUI: { electronCaptionMode },
}) => ({
  ccMode,
  firebaseUser,
  isInConversation,
  status,
  user: captioner,
  sidebar,
  electronCaptionMode,
  sessionLoading: loading,
});

export default connect(mapStateToProps)(Default);
